'use client';
import { HitType } from '@/modules/common/lib/abtastyStub';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import Image from '@/modules/common/components/Image';
import MarkdownRenderer from '@/modules/common/components/MarkdownRenderer';
import { Back } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { useFilteredCollectionProducts } from '@/modules/product/hooks/useFilteredCollectionProducts';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { AddToShoppingListButtonRegular } from '@/modules/shopping-list/components/AddToShoppingListButtonRegular';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SaveType } from '@/modules/shopping-list/models/shoppingListTextItem.interface';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '../../services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '../../services/data-layer-events/utils';

type Props = {
  collection: ContentfulCollection;
};
export const CollectionHeader = ({ collection }: Props) => {
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { sendFeatureFlagTracking } = useFeatureFlag();
  const filteredProducts = useFilteredCollectionProducts(collection?.products);
  const skus = filteredProducts?.map((product) => product.sku) || [];

  const { slug, collectionName: title, newBaseImage } = collection;

  const handleClickBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  const shoppingListData = {
    name: title ?? '',
    key: slug ?? '',
    saveType: SaveType.Collection,
  };

  const wishlistTrackingCallback = () => {
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      method: 'Single',
      type: ShoppingListSaveType.Collection,
      product: {
        slug: slug ?? '',
        title: title ?? '',
      } satisfies CollectionDataLayer,
      where: DataLayerWhere.Collections,
      shoppingListProducts,
      item_list_type: 'Editorial collection',
      item_list_name: title ?? '',
      item_list_id: slug ?? '',
    });

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Collection Saved',
      label: collection.roomType,
    });
  };

  return (
    <div className="mx-auto w-full px-4 md:px-8">
      <div className="relative lg:flex lg:items-center lg:gap-8">
        <div className="text-center lg:mt-5 lg:text-left xl:mt-0">
          <button
            className="flex h-[36px] w-[93px] gap-1 transition-opacity hover:opacity-75 lg:w-[133px]"
            onClick={handleClickBack}
            data-testid="back-button"
          >
            <Back className="h-[24px] w-[24px]" />
            Back
          </button>

          <h1
            className="text-[24px] font-bold xs:text-[28px] lg:text-4xl"
            data-testid="collection-name"
          >
            {collection?.collectionName || ''}
          </h1>
          <p
            className="my-4 text-[13px] text-gray-500 md:text-base"
            data-testid="matching-materials"
          >
            {/* Shows only products that are published */}
            {filteredProducts?.length && filteredProducts?.length > 0 ?
              `${filteredProducts?.length} matching materials`
            : null}
          </p>
          <MarkdownRenderer
            className="mx-auto mb-6 self-center md:mb-8 md:max-w-[544px] lg:max-w-[912px] lg:text-2xl"
            data-testid="collection-description"
          >
            {collection?.description}
          </MarkdownRenderer>

          <div className="mb-6 md:mb-8 lg:mb-0">
            <AddToShoppingListButtonRegular
              saveType={ShoppingListSaveType.Collection}
              textLineItem={shoppingListData}
              image={newBaseImage?.url ?? ''}
              className="w-full md:w-auto"
              sku={skus[0] || undefined}
              callbacks={{ afterAdd: wishlistTrackingCallback }}
              data-testid="add-to-shopping-list-button"
            />
          </div>
        </div>

        {collection?.newBaseImage?.url && (
          <div className="w-full max-w-[700px] lg:max-w-[800px]">
            <Image
              width={800}
              src={collection.newBaseImage?.url}
              alt="Collection Banner"
              className="rounded-xxl"
            />
          </div>
        )}
      </div>
    </div>
  );
};
